/**
 * CodeSnippet — reusable code block with a Copy-to-clipboard button.
 *
 * Extracted from the copy-button pattern used in PartnerSecretRotation.tsx
 * (lines ~32-44 + ~86-112). Used by PartnerIntegrationDocs to render
 * partner-specific snippets staff can hand off to a partner's developer.
 *
 * Falls back gracefully when the Clipboard API rejects (insecure context,
 * permissions): the user can still hand-select the visible code.
 */
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const COPY_FEEDBACK_MS = 2000;

export interface CodeSnippetProps {
  code: string;
  language?: 'bash' | 'typescript' | 'javascript' | 'json' | 'text';
  /** Optional header label, e.g. "sso.js" or "bash". Renders a header bar. */
  label?: string;
  /** Wrap long lines (useful for curl). Default false preserves indentation. */
  wrap?: boolean;
  /** Pass-through container class. */
  className?: string;
}

export function CodeSnippet({
  code,
  language: _language,
  label,
  wrap = false,
  className,
}: CodeSnippetProps): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, COPY_FEEDBACK_MS);
    } catch {
      // Clipboard API can fail (insecure context, permissions). The code
      // remains hand-selectable in the <pre>.
      setCopied(false);
    }
  };

  const copyButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      aria-label="Copy code to clipboard"
      className="h-7 gap-1.5 px-2 text-xs"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </Button>
  );

  return (
    <div
      className={cn(
        'rounded-md border bg-muted/40 overflow-hidden',
        className,
      )}
    >
      {label ? (
        <div className="flex items-center justify-between border-b bg-muted/60 px-3 py-1.5">
          <span className="font-mono text-xs text-muted-foreground">
            {label}
          </span>
          {copyButton}
        </div>
      ) : null}
      <div className="relative">
        {!label ? (
          <div className="absolute right-1.5 top-1.5 z-10">{copyButton}</div>
        ) : null}
        <pre
          className={cn(
            'overflow-x-auto px-3 py-2.5 font-mono text-xs',
            wrap && 'whitespace-pre-wrap break-all',
          )}
        >
          {code}
        </pre>
      </div>
    </div>
  );
}
